import React, { useMemo } from 'react';
import classNames from 'classnames';
import StatusBadge from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/StatusBadge';
import { useTranslation } from 'react-i18next';
import { Listing, ListingTier } from 'models/Listing';
import ResolveImage from 'components/ResolveImage';
import TextBubleIcon from 'icons/TextBuble.icon';
import StarIcon from 'icons/Star.icon';
import { Button } from 'ncoded-component-library';
import PlatinumIcon from 'icons/Platinum.icon';
import ListingPrices from 'components/ListingPrices';
import { getImgUrl, RESIZED_WIDTH } from 'components/ResolveImage/utils';
import EoiDate from 'components/EoiDate';
import { APPOINTMENT_TYPE_COPIES } from 'components/DistressedListingCard/utils';
import ListingContactsCardList from 'components/ListingContactsCardList';
import {
  checkIsTierPlatinum,
  checkIsTierPremium,
  checkIsUrgent,
  getAcceptedListingContacts,
  isNewListing,
} from 'utils';
import ListingLocation from 'components/ListingLocation';
import ListingHighlights from 'components/ListingHighlights';
import PremiumListingGallery from 'components/PremiumListingGallery';
import ListingCardThumbnail from 'components/ListingCardThumbnail';

import 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/ListingCard/ListingCard.styles.scss';
import 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/ListingCard/ListingCard.styles.responsive.scss';
import 'components/PremiumListingCard/PremiumListingCard.styles.scss';
import 'components/PremiumListingCard/PremiumListingCard.styles.responsive.scss';
import 'components/DistressedListingCard/DistressedListingCard.styles.scss';
import 'components/DistressedListingCard/DistressedListingCard.styles.responsive.scss';

type ListingCardTierPreviewProps = {
  className?: string;
  listing: Partial<Listing>;
  showAs: ListingTier;
};

const ListingCardTierPreview: React.FC<ListingCardTierPreviewProps> = (
  props,
) => {
  const {
    className,
    listing: {
      listingContacts,
      createdAt,
      listingTitle,
      location,
      locationPostcode,
      industry,
      revenue,
      highlights,
      gallery,
      averageStockValue,
      businessDescription,
      askingPriceSelect,
      amountFrom,
      amountTo,
      amount,
      EBITDA,
      listingContact,
      stockAtValue,
      type,
      seekingExpressionOfInterestDate,
      appointmentType,
      currency,
      dealLocation,
      amountRequiredVisibleInfo,
      amountFromRequiredVisibleInfo,
      amountToRequiredVisibleInfo,
      revenueRequiredVisibleInfo,
      EBITDARequiredVisibleInfo,
      averageStockValueRequiredVisibleInfo,
    },
    showAs,
  } = props;
  const { t } = useTranslation();

  const isUrgent = checkIsUrgent(type);

  const isPlatinum = checkIsTierPlatinum(showAs);
  const isPremium = checkIsTierPremium(showAs);

  const classes = classNames(
    isPlatinum
      ? 'premium-listing-card'
      : isUrgent
      ? 'urgent-campaigns-listing-card'
      : 'listing-card',
    'listing-card-tier-preview',
    className,
  );

  const firmLogo = listingContact?.firmLogo?.url;

  const acceptedListingContacts = useMemo(
    () => getAcceptedListingContacts(listingContacts),
    [listingContacts],
  );

  const RenderAppointment = useMemo(() => {
    if (!isUrgent) return null;

    return (
      <p className="appointment">
        {t(
          (APPOINTMENT_TYPE_COPIES[
            appointmentType as keyof typeof APPOINTMENT_TYPE_COPIES
          ] as string) || APPOINTMENT_TYPE_COPIES.Other,
          {
            name: `${listingContact?.firstName} ${listingContact?.lastName}`,
          },
        )}
      </p>
    );
  }, [appointmentType, listingContact, t, isUrgent]);

  const RenderActions = useMemo(
    () => (
      <div className="listing-actions">
        <Button variant="outline" icon={TextBubleIcon}>
          {t('enquire')}
        </Button>
        <Button variant="link" icon={StarIcon}>
          {t('shortlist')}
        </Button>
      </div>
    ),
    [t],
  );

  if (isPlatinum)
    return (
      <div className={classes}>
        <span className="premium-listing-card__tier-badge">
          <PlatinumIcon />
          {t('platinum')}
        </span>
        <div className="premium-listing-card__content">
          <div className="premium-listing-card__content__main-content">
            <div className="premium-listing-card__content__main-content__title">
              {listingTitle}
            </div>
            <ListingLocation
              industry={industry}
              isUrgentListing={isUrgent}
              locationPostcode={locationPostcode}
              location={location}
              dealLocation={dealLocation}
            />
            <EoiDate
              seekingExpressionOfInterestDate={seekingExpressionOfInterestDate}
            />
            <ListingPrices
              askingPrice={askingPriceSelect}
              amount={amount}
              amountFrom={amountFrom}
              amountTo={amountTo}
              revenue={revenue}
              EBITDA={EBITDA}
              averageStockValue={averageStockValue}
              stockAtValue={stockAtValue}
              currency={currency}
              amountRequiredVisibleInfo={amountRequiredVisibleInfo}
              amountFromRequiredVisibleInfo={amountFromRequiredVisibleInfo}
              amountToRequiredVisibleInfo={amountToRequiredVisibleInfo}
              revenueRequiredVisibleInfo={revenueRequiredVisibleInfo}
              EBITDARequiredVisibleInfo={EBITDARequiredVisibleInfo}
              averageStockValueRequiredVisibleInfo={
                averageStockValueRequiredVisibleInfo
              }
            />
            <div className="premium-listing-card__content__main-content__description">
              {businessDescription}
            </div>
            <ListingHighlights highlights={highlights} />
          </div>
          <PremiumListingGallery gallery={gallery} />

          {(acceptedListingContacts?.[0]?.name || firmLogo) && (
            <div className="premium-listing-card__content__agent-details">
              <ListingContactsCardList
                listingContacts={acceptedListingContacts}
              />

              {firmLogo && (
                <ResolveImage
                  asCompanyLogo
                  className="premium-listing-card__content__agent-details__image"
                  src={getImgUrl(firmLogo, RESIZED_WIDTH[320])}
                />
              )}
            </div>
          )}
          {RenderActions}
        </div>
      </div>
    );

  if (isUrgent)
    return (
      <div className={classes}>
        <div className="urgent-campaigns-listing-card__left">
          <div className="urgent-campaigns-listing-card__left__heading">
            <StatusBadge isNew={isNewListing(createdAt)} />
            <div className="urgent-campaigns-listing-card__left__heading__title">
              {listingTitle}
            </div>
            <ListingLocation
              industry={industry}
              isUrgentListing
              locationPostcode={locationPostcode}
            />
          </div>
          {RenderAppointment}
          <EoiDate
            seekingExpressionOfInterestDate={seekingExpressionOfInterestDate}
          />
          <div className="urgent-campaigns-listing-card__left__description">
            {businessDescription}
          </div>
          {RenderActions}
        </div>
        <ListingCardThumbnail
          gallery={gallery}
          firmLogo={firmLogo}
          isPremium={isPremium}
        />
      </div>
    );

  return (
    <div className={classes}>
      <div className="listing-card__content">
        <div className="listing-card__content__top">
          <StatusBadge isNew={isNewListing(createdAt)} />
          <div className="listing-card__content__top__title">
            {listingTitle}
          </div>
          <ListingLocation
            industry={industry}
            location={location}
            dealLocation={dealLocation}
          />
          <ListingPrices
            askingPrice={askingPriceSelect}
            amount={amount}
            amountFrom={amountFrom}
            amountTo={amountTo}
            revenue={revenue}
            EBITDA={EBITDA}
            averageStockValue={averageStockValue}
            stockAtValue={stockAtValue}
            currency={currency}
            amountRequiredVisibleInfo={amountRequiredVisibleInfo}
            amountFromRequiredVisibleInfo={amountFromRequiredVisibleInfo}
            amountToRequiredVisibleInfo={amountToRequiredVisibleInfo}
            revenueRequiredVisibleInfo={revenueRequiredVisibleInfo}
            EBITDARequiredVisibleInfo={EBITDARequiredVisibleInfo}
            averageStockValueRequiredVisibleInfo={
              averageStockValueRequiredVisibleInfo
            }
          />
        </div>
        <div className="listing-card__content__description">
          {businessDescription}
        </div>
        {RenderActions}
      </div>
      <ListingCardThumbnail
        gallery={gallery}
        firmLogo={firmLogo}
        isPremium={isPremium}
      />
    </div>
  );
};

export default ListingCardTierPreview;
